import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { tagService, categoryService } from '../services/api';
import { Card, Input, Button, Select, useToast, Textarea } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';

const CreateEditTag = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryType, setCategoryType] = useState('');
  const [categoryCode, setCategoryCode] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const [categoryError, setCategoryError] = useState('');
  
  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    if (isViewMode) {
      setPageTitle('View Tag');
    } else if (isEditMode) {
      setPageTitle('Edit Tag');
    } else {
      setPageTitle('Add Tag');
    }
  }, [isEditMode, isViewMode, setPageTitle]);

  useEffect(() => {
    if (isEditMode || isViewMode) {
      fetchTagData();
    }
  }, [id, isEditMode, isViewMode]);

  const fetchCategories = async (type = '') => {
    try {
      const params = { page: 1, limit: 1000 };
      if (type) {
        params.category_type = type;
      }
      const response = await categoryService.getAll(params);
      setCategories(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleCategoryTypeChange = (e) => {
    const type = e.target.value;
    setCategoryType(type);
    setCategoryCode(''); // Reset category selection when type changes
    if (type) {
      fetchCategories(type);
    } else {
      setCategories([]);
    }
  };

  const fetchTagData = async () => {
    try {
      setLoading(true);
      const response = await tagService.getAll({ page: 1, limit: 1000 });
      const tag = response.data.data.find(t => t.id === parseInt(id));
      
      if (tag) {
        setName(tag.name);
        setDescription(tag.description || '');
        setCategoryCode(tag.category_code);
        
        // Load all categories, including the currently mapped one even if inactive
        const categoriesResponse = await categoryService.getAll({ page: 1, limit: 1000, include_codes: tag.category_code });
        const allCategories = categoriesResponse.data.data || categoriesResponse.data;
        const selectedCategory = allCategories.find(c => c.code === tag.category_code);
        
        if (selectedCategory && selectedCategory.category_type) {
          setCategoryType(selectedCategory.category_type);
          // Filter categories by type
          const filteredCategories = allCategories.filter(c => c.category_type === selectedCategory.category_type);
          setCategories(filteredCategories);
        } else {
          setCategories(allCategories);
        }
      } else {
        toast.error('Tag not found');
        navigate('/admin/tags');
      }
    } catch (err) {
      toast.error('Failed to fetch tag details');
      console.error(err);
      navigate('/admin/tags');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    let isValid = true;
    setNameError('');
    setCategoryError('');
    setError('');

    if (!name.trim()) {
      setNameError('Tag name is required');
      isValid = false;
    }
    if (!categoryType) {
      setCategoryError('Category type is required');
      isValid = false;
    }
    if (!categoryCode) {
      setCategoryError('Category is required');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = {
        name: name.trim(),
        description: description.trim() || null,
        category_code: categoryCode,
      };

      if (isEditMode) {
        await tagService.update(id, data);
        toast.success('Tag updated successfully');
      } else {
        await tagService.create(data);
        toast.success('Tag created successfully');
      }
      
      navigate('/admin/tags');
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} tag`;
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/tags');
  };

  if (loading && (isEditMode || isViewMode) && !name) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading tag details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full" allowOverflow={true}>
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate className="w-full">
          {/* Breadcrumbs Row */}
          <Breadcrumbs
            items={[
              { label: 'Tags', to: '/admin/tags' },
              { label: isViewMode ? 'View Tag' : isEditMode ? 'Edit Tag' : 'Add Tag' }
            ]}
            className="mb-6"
          />

          {/* Description */}
          <div className="mb-6">
            <Textarea
              label="Description"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter tag description (used for AI matching accuracy)"
              rows={3}
              disabled={loading || isViewMode}
            />
          </div>

          {/* Tag Name and Category Type/Category in a grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <Input
                label="Tag Name"
                name="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError('');
                }}
                placeholder="Enter tag name"
                required
                error={nameError}
                disabled={loading || isViewMode}
              />
            </div>
            <div>
              <Select
                label="Category Type"
                name="categoryType"
                value={categoryType}
                onChange={handleCategoryTypeChange}
                required
                error={!categoryType && categoryError}
                disabled={loading || isViewMode}
              >
                <option value="">Select Category Type</option>
                <option value="Software">Software</option>
                <option value="Hardware">Hardware</option>
              </Select>
            </div>
            <div>
              <Select
                label="Category"
                name="categoryCode"
                value={categoryCode}
                onChange={(e) => {
                  setCategoryCode(e.target.value);
                  if (categoryError) setCategoryError('');
                }}
                required
                error={categoryError}
                disabled={loading || isViewMode || !categoryType}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.code} value={cat.code}>
                    {cat.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Actions Row: Buttons at far right */}
          {!isViewMode && (
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="border-primary-600 text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {loading ? 'Saving...' : isEditMode ? 'Update Tag' : '+ Add Tag'}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default CreateEditTag;
